import { Delete } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Cafe } from '../../apiTypes';
import Form from '../../components/forms/Form';
import FormPriceField from '../../components/forms/FormPriceField';
import FormSwitch from '../../components/forms/FormSwitch';
import FormTextField from '../../components/forms/FormTextField';
import { ProFeature } from '../../components/PlanCheck/PlanCheckPage';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { updateDoc } from '../../utils/api';

const LoyaltyProgram = () => {
  const { cafe, cafeSlug } = useAdmin();
  const { lang } = useLocale();

  const form = useForm<NonNullable<Cafe['loyalty']>>({
    defaultValues: {
      enabled: cafe?.loyalty?.enabled || false,
      matrix: cafe?.loyalty?.matrix || [
        { sum: 5_000_00, discount: 1 },
        { sum: 10_000_00, discount: 2 },
      ],
    },
    reValidateMode: 'onChange',
    mode: 'onChange',
  });

  const {
    watch,
    control,
    formState: { errors },
    setValue,
  } = form;
  const enabled = watch('enabled');
  const matrix = watch('matrix');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'matrix',
    keyName: 'id',
  });

  const submit = useCallback(
    async (values: NonNullable<Cafe['loyalty']>) => {
      const res = await updateDoc<Cafe>(`cafes/${cafeSlug}`, {
        loyalty: values,
      });
      form.reset(values);
    },
    [cafeSlug, form]
  );

  return (
    <Form form={form} onSubmit={submit}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <PrimaryButton variant="contained" type="submit" disabled={!form.formState.isDirty}>
          {t.save_changes[lang]}
        </PrimaryButton>
      </Box>

      <ProFeature>
        <FormSwitch name="enabled" color="primary" label={t.cumulative_discount_system[lang]} />
      </ProFeature>

      <Box sx={{ mt: 2 }}>
        {fields.map((field, index) => (
          <Box key={field.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography sx={{ color: (t) => (enabled ? t.palette.text.primary : t.palette.text.disabled) }}>
              {index + 1}
            </Typography>

            <Box sx={{ width: 180 }}>
              <FormPriceField
                label={t.spent_amount[lang]}
                name={`matrix[${index}].sum`}
                size="small"
                disabled={!enabled}
                InputProps={{ endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment> }}
                error={!!errors.matrix?.[index]?.sum}
                rules={{
                  validate: (v) => Number(v) > Number(matrix[index - 1]?.sum || 0),
                }}
              />
            </Box>

            <Box sx={{ width: 100 }}>
              <FormTextField
                label={t.discount[lang]}
                name={`matrix[${index}].discount`}
                size="small"
                type="number"
                disabled={!enabled}
                error={!!errors.matrix?.[index]?.discount}
                rules={{
                  validate: (v) => Number(v) >= Number(matrix[index - 1]?.discount || 0),
                }}
                onChange={(e) => {
                  const discount = Number(e.target.value) || '';

                  if (discount === '' || (discount >= 0 && discount <= 100)) {
                    setValue(`matrix[${index}].discount` as 'matrix.1.discount', discount as 0, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Box>

            <IconButton
              disabled={!enabled}
              sx={{ width: 32, height: 32, flexShrink: 0 }}
              size="small"
              onClick={() => remove(index)}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}

        <PrimaryButton
          sx={{ ml: 2 }}
          size="small"
          disabled={!enabled}
          onClick={() =>
            append({
              sum: (fields[fields.length - 1]?.sum || 0) + 5_000_00,
              discount: (fields[fields.length - 1]?.discount || 0) + 1,
            })
          }
        >
          {t.add_step[lang]}
        </PrimaryButton>
      </Box>
    </Form>
  );
};

export default LoyaltyProgram;
